import { Html } from "@react-three/drei";
import React, { useCallback } from "react";
import Lottie from "react-lottie";
import styled from "styled-components";
import useTransitionStore from "../../../state/transitions";
import useCustomizerStore from "../../../state/customizer";
import reveal from "../../../assets/animations/reveal.json";
import clicked from "../../../assets/animations/pressed.json";
import clickedZoom from "../../../assets/animations/clicked2.json";
import idle from "../../../assets/animations/idle.json";
import idleExit from "../../../assets/animations/idle-exit.json";
import { useRef } from "react";
import { isMobile, useMobileOrientation } from "react-device-detect";
import { defaultControlAngles } from "../../../util/defaultControlAngles";
import { AnimatePresence, motion, MotionConfig } from "framer-motion";

const Hotspot = ({ e, id, position, handleClick, selected, switchSides }) => {
  const activeHotspot = useRef(null);
  const CameraSide = useTransitionStore((s) => s.CameraSide);
  const DisplayMode = useTransitionStore((s) => s.DisplayMode);
  const HotspotZoomed = useTransitionStore((s) => s.HotspotZoomed);
  const ShowScrollContainer = useTransitionStore((s) => s.ShowScrollContainer);
  const setShowScrollContainer = useTransitionStore(
    (s) => s.setShowScrollContainer
  );
  const setHotspotZoomed = useTransitionStore((s) => s.setHotspotZoomed);
  const TriggerExitHotspot = useTransitionStore((s) => s.TriggerExitHotspot);
  const setTriggerExitHotspot = useTransitionStore(
    (s) => s.setTriggerExitHotspot
  );
  const setHotspotControls = useTransitionStore((s) => s.setHotspotControls);
  const setAzimuthAndPolarAngles = useTransitionStore(
    (s) => s.setAzimuthAndPolarAngles
  );
  const setTempAzimuthAndPolarAngles = useTransitionStore(
    (s) => s.setTempAzimuthAndPolarAngles
  );
  const setSuspensionMode = useTransitionStore((s) => s.setSuspensionMode);
  const SuspensionMode = useTransitionStore((s) => s.SuspensionMode);
  const setDisplayMode = useTransitionStore((s) => s.setDisplayMode);
  const setCamIndex = useTransitionStore((s) => s.setCamIndex);
  const setSelectedHotspot = useCustomizerStore((s) => s.setSelectedHotspot);
  const filterHotspots = useCustomizerStore((s) => s.filterHotspots);
  const setFilterHotspots = useCustomizerStore((s) => s.setFilterHotspots);
  const setFutureSelectedHotspotDescription = useCustomizerStore(
    (s) => s.setFutureSelectedHotspotDescription
  );
  const setFutureSelectedHotspotTitle = useCustomizerStore(
    (s) => s.setFutureSelectedHotspotTitle
  );
  const setDisclaimerOpen = useCustomizerStore((s) => s.setDisclaimerOpen);
  const setSelectedDisclaimer = useCustomizerStore(
    (s) => s.setSelectedDisclaimer
  );
  const setfutureSelectedDisclaimer = useCustomizerStore(
    (s) => s.setfutureSelectedDisclaimer
  );

  const { isLandscape } = useMobileOrientation();

  const handleExitClick = useCallback(() => {
    if (selected) {
      //todo: pass down from parent
      setSelectedHotspot("");
      setFutureSelectedHotspotDescription("");
      setFutureSelectedHotspotTitle("");
      setfutureSelectedDisclaimer("");
      setDisclaimerOpen(false);
      setShowScrollContainer(false);
      if (filterHotspots) setFilterHotspots(false);
      if (SuspensionMode) {
        setSuspensionMode(false);
        setTriggerExitHotspot(!TriggerExitHotspot);
      }
      if (!HotspotZoomed) return;
      setAzimuthAndPolarAngles(defaultControlAngles);
      setTriggerExitHotspot(!TriggerExitHotspot);
      // setTempAzimuthAndPolarAngles(defaultControlAngles);
      setHotspotControls(false);
      setHotspotZoomed(false);
    } else {
      handleClick(id);
    }
  });

  return (
    <>
      <Html
        style={{
          cursor: "pointer",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          pointerEvents: "auto",
        }}
        distanceFactor={
          //20
          DisplayMode == "interior"
            ? 40
            : isMobile && !isLandscape
            ? DisplayMode == "powertrain"
              ? 3
              : HotspotZoomed
              ? 1
              : 5
            : HotspotZoomed
            ? 1
            : 3
        }
        position={[
          switchSides && CameraSide == "driver" && !selected
            ? position[0]
            : -position[0],
          position[1],
          position[2],
        ]}
        rotation={[0, Math.PI / 2, 0]}
        zIndexRange={[0, 100]}
      >
        <MotionConfig transition={{ duration: 0.5 }}>
          <AnimatePresence>
            {selected ? (
              <Div key={`selected-${id}`} onClick={handleExitClick}>
                <Lottie
                  // key={"lottie" + id}
                  width={70}
                  options={{
                    loop: false,
                    autoplay: true,
                    animationData:
                      DisplayMode === "exterior"
                        ? id === 1 ||
                          id === 3 ||
                          id === 7 ||
                          id === 8 ||
                          id === 4
                          ? clickedZoom
                          : clicked
                        : clicked,
                    rendererSettings: {
                      preserveAspectRatio: "xMidYMid slice",
                    },
                  }}
                  eventListeners={[
                    {
                      eventName: "complete",
                      callback: () => (activeHotspot.current = id),
                    },
                  ]}
                />
              </Div>
            ) : activeHotspot.current === id ? (
              <Div key={`idle-${id}`} onClick={() => handleClick(id)}>
                <Lottie
                  width={70}
                  options={{
                    loop: false,
                    autoplay: true,
                    animationData: idle,
                    rendererSettings: {
                      preserveAspectRatio: "xMidYMid slice",
                    },
                  }}
                />
              </Div>
            ) : (
              <Div key={`reveal-${id}`} onClick={() => handleClick(id)}>
                <Lottie
                  width={70}
                  options={{
                    loop: false,
                    autoplay: true,
                    animationData: reveal,
                    rendererSettings: {
                      preserveAspectRatio: "xMidYMid slice",
                    },
                  }}
                />
              </Div>
            )}
          </AnimatePresence>
        </MotionConfig>
      </Html>
    </>
  );
};

const Div = styled(motion.div)`
  transition: transform 0.2s ease-in-out;
  transform: scale(1);
  position: absolute;

  &:hover {
    transform: scale(1.2);
  }
`;

export default React.memo(Hotspot);
